import React, { useEffect, useState } from 'react';
import { NodeViewComponentProps } from '@remirror/extension-react-component';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  VStack,
} from '@cardboard-ui/react';
import LinkCardMenu from './LinkCardMenu';
import { useCommands, useEditorView } from '@remirror/react-core';
import { t } from '@lingui/macro';
import { OverlayProps } from 'components/TaskOverlay';
import { AlertDialog } from 'components/dialogs/AlertDialog';
import { LinkPreview } from './LinkPreview';
import * as Sentry from '@sentry/react';
import { getGenericMetadata, getUrlInfo } from 'utils/urlInfo';

export default function LinkCard(props: NodeViewComponentProps) {
  const commands = useCommands();
  const editorView = useEditorView();
  const {
    isOpen: isSetLinkOpen,
    onOpen: onOpenSetLink,
    onClose: onCloseSetLink,
  } = useDisclosure();

  useEffect(() => {
    if (props.node.attrs?.metadata || !props.node.attrs?.url) {
      return;
    }
    getUrlInfo(props.node.attrs.url)
      .then((res) => {
        commands.setLinkCardMetadata(getPosition(), res);
      })
      .catch((err) => {
        console.error(err);
        Sentry.withScope((s) => {
          s.setLevel('warning');
          s.setTag('linkCard', 'fetchMetadata');
          Sentry.captureException(err);
        });
      });
  }, [props.node.attrs?.metadata]);

  const getPosition = props.getPosition as () => number;
  const setLinkCard = (url: string) =>
    commands.setLinkCardUrl(getPosition(), url);

  const deleteLinkCard = () => commands.deleteLinkCard(getPosition());

  if (!props.node.attrs?.url) {
    return null;
  }

  return (
    <Box pos="relative" w="100%">
      <LinkPreview
        url={props.node.attrs?.url}
        fetcher={() => new Promise(() => {})}
        showLoader
        metadata={
          props.node.attrs?.metadata || getGenericMetadata(props.node.attrs.url)
        }
      />
      {editorView.editable && (
        <>
          <Box m={1} pos="absolute" right="0" top="0" borderRadius="50%">
            <LinkCardMenu onOpenSetLink={onOpenSetLink} />
          </Box>
          <SetUrlModal
            isOpen={isSetLinkOpen}
            onClose={onCloseSetLink}
            setLinkCard={setLinkCard}
            deleteLinkCard={deleteLinkCard}
            url={props.node.attrs?.url}
          />
        </>
      )}
    </Box>
  );
}

interface SetUrlModalProps extends OverlayProps {
  setLinkCard: (url: string) => void;
  deleteLinkCard?: () => void;
  url?: string;
}

export const SetUrlModal = ({
  setLinkCard,
  deleteLinkCard,
  url,
  ...overlayProps
}: SetUrlModalProps) => {
  const { onClose } = overlayProps;
  const [linkValue, setLinkValue] = useState<string>(url || '');

  const handleActionClick = async () => {
    setLinkCard(linkValue);
    onClose();
  };

  const handleClose = () => {
    if (!linkValue && !url && deleteLinkCard) {
      deleteLinkCard();
    }
    onClose();
  };

  let isValidUrl;
  try {
    const url = new URL(linkValue);
    isValidUrl = true;
  } catch (e) {
    isValidUrl = false;
  }
  return (
    <AlertDialog
      {...overlayProps}
      title={t`Create Link Card`}
      cancelButton={{ title: t`Cancel`, onClick: handleClose }}
      actionButton={{
        title: t`Save`,
        onClick: () => handleActionClick(),
        isDisabled: !isValidUrl,
      }}
    >
      <VStack>
        <FormControl>
          <FormLabel htmlFor="link">
            {t`Enter a link to create a link card. We'll try to fetch the title, description, and image.`}
          </FormLabel>
          <Input
            id="link"
            type="url"
            value={linkValue}
            onChange={({ target: { value } }) => setLinkValue(value)}
            isInvalid={!isValidUrl}
            placeholder="https://example.com"
            autoFocus
          />
        </FormControl>
      </VStack>
    </AlertDialog>
  );
};
